// StorageContext.tsx - Fixed version with better error handling
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { Storage } from "@ionic/storage";
import { initStorage } from "../services/storageService";

interface StorageContextType {
  storage: Storage | null;
  isStorageReady: boolean;
  error: string | null;
}

// Create context with default values
const StorageContext = createContext<StorageContextType>({
  storage: null,
  isStorageReady: false,
  error: null,
});

// Custom hook with better error handling
export const useStorage = (): StorageContextType => {
  const context = useContext(StorageContext);

  // Better error message for debugging
  if (context === undefined) {
    throw new Error(
      "useStorage must be used within a StorageProvider. " +
        "Make sure your component is wrapped with <StorageProvider>. " +
        "Check your App.tsx file and ensure StorageProvider is properly imported and used."
    );
  }

  return context;
};

interface StorageProviderProps {
  children: ReactNode;
}

export const StorageProvider: React.FC<StorageProviderProps> = ({
  children,
}) => {
  const [storage, setStorage] = useState<Storage | null>(null);
  const [isStorageReady, setIsStorageReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeStorage = async () => {
      try {
        console.log("StorageProvider: Initializing storage...");
        const storageInstance = await initStorage(null);
        setStorage(storageInstance);
        setIsStorageReady(true);
        setError(null);
        console.log("StorageProvider: Storage initialized successfully");
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown storage error";
        console.error(
          "StorageProvider: Failed to initialize storage:",
          errorMessage
        );
        setError(errorMessage);
        setIsStorageReady(false);
      }
    };

    initializeStorage();
  }, []);

  const contextValue: StorageContextType = {
    storage,
    isStorageReady,
    error,
  };

  return (
    <StorageContext.Provider value={contextValue}>
      {children}
    </StorageContext.Provider>
  );
};

// Optional: Enhanced hook with waiting capability
export const useStorageOperations = () => {
  const { storage, isStorageReady, error } = useStorage();

  const isReady = () => storage && isStorageReady && !error;

  const waitForStorage = (): Promise<Storage> => {
    return new Promise((resolve, reject) => {
      if (isReady()) {
        resolve(storage!);
        return;
      }

      if (error) {
        reject(new Error(`Storage initialization failed: ${error}`));
        return;
      }

      // Poll for storage to be ready (with timeout)
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait

      const checkStorage = () => {
        attempts++;
        if (isReady()) {
          resolve(storage!);
        } else if (error) {
          reject(new Error(`Storage initialization failed: ${error}`));
        } else if (attempts >= maxAttempts) {
          reject(new Error("Storage initialization timeout"));
        } else {
          setTimeout(checkStorage, 100);
        }
      };

      checkStorage();
    });
  };

  return {
    storage,
    isStorageReady,
    error,
    isReady,
    waitForStorage,
  };
};
